import React, { FC, useEffect, useCallback, useMemo, useState, Fragment } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import makeStyles from '@mui/styles/makeStyles';
import prettyBytes from 'pretty-bytes'

import { useDropzone } from 'react-dropzone'

import Avatar from '@mui/material/Avatar'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Grid from '@mui/material/Grid'
import Box from '@mui/material/Box'
import Breadcrumbs from '@mui/material/Breadcrumbs'
import Filters from '../../components/page_layout/Filters'
import DataGridWithFilters from '../../components/page_layout/DataGridWithFilters'

import Loading from '../../components/system/Loading'
import ClickLink from '../../components/widgets/ClickLink'
import Table from '../../components/table/SimpleTable'
import UploadStatus from '../../components/uploader/UploadStatus'
import TinyButton from '../../components/widgets/TinyButton'
import SectionHeader from '../../components/widgets/SectionHeader'
import Window from '../../components/dialog/Window'
import WithRole from '../../components/rbac/WithRole'
import FAIcon from '../../components/widgets/FAIcon'

import FileDataGrid from './FileDataGrid'

import authSelectors from '../../store/selectors/auth'
import projectSelectors from '../../store/selectors/project'
import filestoreSelectors from '../../store/selectors/filestore'
import filestoreActions from '../../store/modules/filestore'
import fileuploadSelectors from '../../store/selectors/fileupload'

import {
  getFilename,
} from '@kaidam/fc-shared/src/utils/file'

import {
  hasRole,
} from '@kaidam/fc-shared/src/utils/user_summary'

import {
  getPathBreadcrumbs,
  StorageFile,
} from '@kaidam/fc-shared/src/utils/storage'

import {
  IForm_File,
} from '@kaidam/fc-shared/src/types/schema/form'

import {
  IField_Filestore_Type,
  IField_Storage_Location,
} from '@kaidam/fc-shared/src/types/storage'

import {
  ThunkDispatcher,
} from '../../types/store'

import {
  TableSchema,
} from '../../types/app'

import {
  FILESTORE_TYPE_DRIVE_PUBLIC,
  FILESTORE_ROLES,
  FILESTORE_TITLES,
} from '@kaidam/fc-shared/src/constants'

import {
  FC_BUTTON_ICON_SIZE,
} from '@kaidam/fc-shared/src/constants/mui'

import icons from '../../icons'
import { width } from '@mui/system';

const FolderIcon = icons.folder

const tableSchema: TableSchema = [{
  title: '',
  name: 'icon',
  width: 30,
}, {
  title: 'Name',
  name: 'name',
}, {
  title: 'Size',
  name: 'size',
}, {
  title: 'Updated',
  name: 'updated',
}]

interface EditMode {
  mode: 'none' | 'add' | 'edit',
  id: string,
}

const DEFAULT_EDIT_MODE: EditMode = {
  mode: 'none',
  id: '',
}

interface FilestoreSectionProps {
  path: string,
  section: IField_Filestore_Type,
  displayFilter?: {
    (file: string): boolean,
  },
  selectFilter?: {
    (file: string): boolean,
  },
  onSetPath: {
    (path: string): void,
  },
  onChooseFile?: {
    (file: IForm_File): void,
  },
}

const FilestoreSection: FC<React.PropsWithChildren<FilestoreSectionProps>> = ({
  path = '',
  section,
  onSetPath,
  onChooseFile,
  displayFilter,
  selectFilter,
}) => {
  const dispatch = useDispatch()
  const thunkDispatch = dispatch as ThunkDispatcher

  const [ editValue, setEditValue ] = useState('')
  const [ editMode, setEditMode ] = useState(DEFAULT_EDIT_MODE)
  const [ deleteFile, setDeleteFile ] = useState<IForm_File | null>(null)

  const userSummary = useSelector(projectSelectors.summary)
  const uploadInProgress = useSelector(fileuploadSelectors.inProgress)
  const uploadStatus = useSelector(fileuploadSelectors.status)
  const uploadError = useSelector(fileuploadSelectors.network.uploadFiles.error)
  const token = useSelector(authSelectors.token)
  const list = useSelector(filestoreSelectors.list)
  const loading = useSelector(filestoreSelectors.network.list.loading)
  
  const writerRole = FILESTORE_ROLES[section]
  const hasWriterRole = hasRole(userSummary, writerRole)

  const isQuickUpload = section == FILESTORE_TYPE_DRIVE_PUBLIC

  const location = useMemo<IField_Storage_Location>(() => {
    return {
      type: section,
      path,
    }
  }, [
    section,
    path,
  ])
  
  const onResetEdit = useCallback(() => {
    setEditMode(DEFAULT_EDIT_MODE)
    setEditValue('')
  }, [])

  const onSaveEdit = useCallback(async () => {
    if(editMode.mode == 'add') {
      const result = await thunkDispatch(filestoreActions.addFolder({
        location,
        name: editValue,
      }))
      if(result) {
        onResetEdit()
      }
    }
    else {
      const result = await thunkDispatch(filestoreActions.rename({
        location,
        renameTo: editValue,
      }))
      if(result) {
        onResetEdit()
      }
    }
  }, [
    location,
    editMode,
    editValue,
  ])

  const onAddFolder = useCallback(() => {
    setEditMode({
      mode: 'add',
      id: 'new',
    })
    setEditValue('')
  }, [])

  const onViewFolder = useCallback((path: any) => {
    onSetPath(path)
  }, [
    path,
  ])

  const onEditItem = useCallback((item: IForm_File) => {
    setEditMode({
      mode: 'edit',
      id: item.pointer.path,
    })
    setEditValue(getFilename(item.pointer.path))
  }, [])

  const onDeleteItem = useCallback((item: IForm_File) => {
    setDeleteFile(item)
  }, [])

  const onConfirmDelete = useCallback(async () => {
    if(!deleteFile) return
    const result = await thunkDispatch(filestoreActions.deleteItem(deleteFile.pointer))
    if(result) setDeleteFile(null)
  }, [
    deleteFile,
  ])

  const onCancelDelete = useCallback(() => {
    setDeleteFile(null)
  }, [])

  const onUploadFiles = useCallback(async (files: File[]) => {
    if(files.length <= 0) return
    if(isQuickUpload) {
      const result = await thunkDispatch(filestoreActions.uploadFiles({
        location,
        files,
      }))
      if(result && result.length > 0 && onChooseFile) {
        onChooseFile(result[0])
      }
    }
    else {
      const result = await thunkDispatch(filestoreActions.upload({
        location,
        files,
      }))
      if(result) {
        dispatch(filestoreActions.list(location))
      }
    }    
  }, [
    location,
    isQuickUpload,
    section,
    path,
  ])

  const isFileFolder = useCallback((file: IForm_File) => {
    const storageFile = StorageFile(file, token)
    return storageFile.isFolder()
  }, [
    token,
  ])

  const processedList = useMemo(() => {
    const folders = list.filter(file => isFileFolder(file))
    const files = list.filter(file => !isFileFolder(file))
    return folders
      .concat(files)
      .filter(file => {
        if(!displayFilter) return true
        return displayFilter(file.meta.filename)
      })
  }, [
    list,
    isFileFolder,
  ])

  const {
    getRootProps,
    getInputProps,
    inputRef,
  } = useDropzone({
    onDrop: onUploadFiles,
    noClick: true,
    noKeyboard: true,
    multiple: true,
  })

  const getActionsHandler = useCallback((item: any) => {
    if(onChooseFile) {
      if(selectFilter && !selectFilter(item.data && item.data.meta && item.data.meta.filename ? item.data.meta.filename : '')) return null
      return (
        <TinyButton
          color="secondary"
          onClick={ () => onChooseFile(item.data) }
        >
          Select
        </TinyButton>
      )
    }
    return hasWriterRole ? (
      <>
        <TinyButton
          onClick={ () => onEditItem(item.data) }
        >
          Rename
        </TinyButton>
        <TinyButton
          onClick={ () => onDeleteItem(item.data) }
          color="error"
        >
          Delete
        </TinyButton>
      </>
    ) : null
  }, [
    hasWriterRole,
    onChooseFile,
    selectFilter,
  ])

  const onOpenUploader = useCallback(() => {
    if(!inputRef.current) return
    inputRef.current.click()
  }, [])

  useEffect(() => {
    dispatch(filestoreActions.setList([]))
    if(!section) return
    if(isQuickUpload) return
    dispatch(filestoreActions.list(location))
  }, [
    location,
  ])

  const dataTable = loading ? (
    <Loading />
  ) : (
    isQuickUpload ? null : (
      <FileDataGrid
        files={ processedList }
        token={ token }
        loading={ false }
        onChooseItem={ onChooseFile }
        onViewFolder={ onViewFolder }
        onEditItem={ onEditItem }
        onDeleteItem={ onDeleteItem }
      />
    )
  )

  const filters = (
    <Filters
      topButtons={
        <WithRole
          name={ writerRole }
        >
          {
            !isQuickUpload && (
              <Box
                sx={{
                  mb: 1,
                  width: '100%',
                }}
              >
                <TinyButton
                  color='primary'
                  sx={{
                    width: '100%',
                  }}
                  onClick={ onAddFolder }
                >
                  New Folder
                </TinyButton>
              </Box>
            )
          }
          <Box
            sx={{
              width: '100%',
            }}
          >
            <TinyButton
              color='secondary'
              sx={{
                width: '100%',
              }}
              onClick={ onOpenUploader }
            >
              Upload File
            </TinyButton>
          </Box>
          <Box
            onClick={ onOpenUploader }
            sx={{
              cursor: 'pointer',
              width: '100%',
              p: 3,
              textAlign: 'center',
            }}
          >
            <FAIcon
              iconName='upload'
              id="Create new article"
              color="#999"
              tooltip='Click or drag files here to upload'
              fontSize='2rem'
              
            />
            <Typography
              variant="h5"
              color='#999'
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                textAlign: 'center',
              }}
            >
              <span>Click or drag files here to upload</span>
            </Typography>
          </Box>
        </WithRole>
      }
    />
  )


  const mainContent = (
    <DataGridWithFilters
      filters={ filters }
      datagrid={ dataTable }
    />
  )

  if(!hasWriterRole) return mainContent

  return (
    <Box
      {...getRootProps()}
      sx={{
        width: '100%',
        height: '100%',
      }}
    >
      { mainContent }
      <input {...getInputProps()} />
      {
        uploadInProgress && (
          <Window
            open
            size="sm"
            withCancel={ false }
            onCancel={ () => {} }
          >
            <Box
              sx={{
                p: 2,
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <UploadStatus
                    error={ uploadError }
                    status={ uploadStatus }
                  />
                </Grid>
              </Grid>
            </Box>
          </Window>
        )
      }
      {
        editMode.mode != 'none' && (
          <Window
            open
            size="sm"
            withCancel
            onSubmit={ onSaveEdit }
            onCancel={ onResetEdit }
          >
            <TextField
              label="Name"
              value={ editValue }
              fullWidth
              onChange={ (e) => setEditValue(e.target.value) }
            />
          </Window>
        )
      }
      {
        deleteFile && (
          <Window
            open
            size="sm"
            title="Confirm Delete"
            withCancel
            submitTitle="Delete"
            onSubmit={ onConfirmDelete }
            onCancel={ onCancelDelete }
          >
            <Typography>
              Are you absolutely sure you want to delete { deleteFile.meta.filename }?
            </Typography>
          </Window>
        )
      }
    </Box>
  )
}

export default FilestoreSection